import { useContext, useEffect, useState, useMemo } from "react";
import { Box, Container, Typography, Alert, useMediaQuery, useTheme, Stack } from "@mui/material";
import { useStorageStore } from "../Stores/StorageStore";
import NotificationWidget from "./NotificationWidget";
import StorageSelector from "./StorageSelector";
import OverallDashboard from "./OverallMetrics/OverallDashboard";
import UserContext from "../User/UserSettings/UserContext";
import { Storage } from "../Types/Storage";
import HealthWidgetBase from "./StorageHealthWidget/HealthWidgetBase";
import RecentServicesWidget from "./RecentServicesWidget";
import MobileStorageCard from "./MobileStorageCard";

function Home() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const { user } = useContext(UserContext) || { user: null, locations: [] };
    const {
        storages, selectedStorageId, notifications, loading, servicesLoading,
        notificationsLoading, error, setSelectedStorage, dismissNotification,
        getSelectedStorage, setStorages, loadNotifications, getRecentServices,
        healthDataByStorage } = useStorageStore();

    const selectedStorage = getSelectedStorage();
    const [loadedStorageCount, setLoadedStorageCount] = useState(1); // Start with 1 to show selected storage

    // Helper function to format alarm strings
    const formatAlarmString = (alarmStr: string): string => {
        // Check if it contains '=' to extract the descriptive part
        if (alarmStr.includes('=')) {
            return alarmStr.split('=')[1].trim();
        }
        // Otherwise, convert from SNAKE_CASE to Title Case
        return alarmStr
            .replace(/^WARN_|^ERROR_|^ALARM_/i, '') // Remove common prefixes
            .replace(/_/g, ' ') // Replace underscores with spaces
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Aggregate health data for the OverallDashboard
    const aggregatedHealthData = useMemo(() => {
        return storages.map(storage => {
            const healthData = healthDataByStorage[storage.id];
            return {
                storageId: storage.id,
                storageName: storage.name,
                healthIndex: healthData?.healthIndex ?? null,
                capacity: healthData?.capacity ?? null
            };
        });
    }, [storages, healthDataByStorage]);

    // Generate alarm notifications from health data
    const alarmNotifications = useMemo(() => {
        const alarmNotifs: any[] = [];

        storages.forEach(storage => {
            const healthData = healthDataByStorage[storage.id];
            const alarms = (healthData as any)?.iotData?.alarmData?.alarms;
            if (alarms && Array.isArray(alarms) && alarms.length > 0) {
                // Format all alarms for better readability
                const formattedAlarms = alarms.map(formatAlarmString);
                
                // Create a concise message
                let message = '';
                if (formattedAlarms.length === 1) {
                    message = formattedAlarms[0];
                } else if (formattedAlarms.length <= 3) {
                    message = formattedAlarms.join('; ');
                } else {
                    message = `${formattedAlarms.slice(0, 2).join('; ')} and ${formattedAlarms.length - 2} more...`;
                }

                alarmNotifs.push({
                    id: `alarm-${storage.id}`,
                    title: `Storage Alert: ${storage.name}`,
                    message: message,
                    type: 'alarm',
                    timestamp: new Date(),
                    priority: 'high',
                    storageId: storage.id,
                    storageName: storage.name,
                    alarms: formattedAlarms, // Store formatted alarms for hover display
                    dismissable: false
                });
            }
        });

        return alarmNotifs;
    }, [storages, healthDataByStorage]);

    // Combine alarm notifications with other notifications
    const allNotifications = useMemo(() => {
        return [...alarmNotifications, ...notifications];
    }, [alarmNotifications, notifications]);

    useEffect(() => {
        // Load storage data
        const storagesArray: Storage[] = [];
        user?.locations?.forEach((storage: { id: number, locationName: string }) => {

            storagesArray.push({
                id: storage.id.toString(),
                name: storage.locationName,
            });
        })
        setStorages(storagesArray);

        // Load notifications if user is available
        if (user?.id) {
            loadNotifications(user.id.toString());
        }
    }, [user, setStorages, loadNotifications]);

    // Lazy load storages one at a time every 1 second
    useEffect(() => {
        if (loadedStorageCount >= storages.length) {
            return; // All storages already loaded
        }

        const delay = isMobile ? 500 : 1000;

        const timer = setTimeout(() => {
            setLoadedStorageCount(prev => prev + 1);
        }, delay); // Load one more storage every 1 second

        return () => clearTimeout(timer);
    }, [loadedStorageCount, storages.length, isMobile]);

    if (loading) {
        return (
            <Container>
                <Typography>Loading...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={3} mb={3}>
                {/* Left Column - Title, Description, and Storage Selector */}
                <Box flex="1" display="flex" flexDirection="column" gap={2}>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            Storage Management Dashboard
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            Monitor and manage your potato storage facilities with real-time data and insights.
                        </Typography>
                    </Box>

                    <Box>
                        <StorageSelector
                            storages={storages}
                            selectedStorageId={selectedStorageId}
                            onStorageChange={setSelectedStorage}
                        />
                    </Box>

                    {/* Overall Dashboard Metrics */}
                    <Box>
                        <OverallDashboard
                            storageData={aggregatedHealthData}
                            onStorageSelect={setSelectedStorage}
                        />
                    </Box>
                </Box>

                {/* Right Column - Notifications Widget (same height as left column) */}
                {!user?.mobile && (
                    <Box flex="0 0 auto" minWidth={{ xs: '100%', lg: '400px' }} maxWidth={{ xs: '100%', lg: '500px' }}>
                        <NotificationWidget
                            notifications={allNotifications}
                            onDismissNotification={dismissNotification}
                            onStorageSelect={setSelectedStorage}
                            loading={notificationsLoading}
                        />
                    </Box>
                )}
            </Box>

            {/* Main Content Grid */}
            {isMobile ? (
                <Stack spacing={2} mb={3}>
                    {storages
                        .slice(0, loadedStorageCount)
                        .map((storage) => {
                            const healthData = healthDataByStorage[storage.id];
                             return (
                                <MobileStorageCard 
                                    key={storage.id}
                                    storage={storage}
                                    healthIndex={healthData?.healthIndex}
                                    currentMode={(healthData as any)?.iotData?.currentMode?.mode}
                                    recentServices={getRecentServices(storage.id)}
                                    servicesLoading={servicesLoading}
                                />
                            );
                        })
                    }
                </Stack>
            ) : (
                <>
            {/* Show selected storage first */}
            {selectedStorage && (
                <Box key={selectedStorage.id} display="flex" gap={3} flexWrap="wrap" alignItems="stretch" mb={3}>
                    {/* Left Column - Crop Health Widget */}
                    <Box flex="2" minWidth={{ xs: '100%', md: '500px' }} maxWidth={{ xs: '100%', lg: 'calc(66% - 1rem)' }}>
                        <HealthWidgetBase storage={selectedStorage} />
                    </Box>
                    {/* Right Column - Recent Services */}
                    <Box
                        flex="1"
                        minWidth={{ xs: '100%', md: '400px' }}
                        maxWidth={{ xs: '100%', lg: 'calc(34% - 1rem)' }}
                    >
                        {/* Recent Services Widget */}
                        <Box sx={{ minHeight: '300px' }}>  
                            <RecentServicesWidget
                                services={getRecentServices(selectedStorage.id)}
                                loading={servicesLoading}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Show remaining storages below */}
            {storages
                ?.filter(storage => storage.id !== selectedStorageId)
                .slice(0, loadedStorageCount - 1) // Gradually show more storages
                .map((storage) => (
                    <Box key={storage.id} display="flex" gap={3} flexWrap="wrap" alignItems="stretch" mb={3}>
                        {/* Left Column - Crop Health Widget */}
                        <Box flex="2" minWidth={{ xs: '100%', md: '500px' }} maxWidth={{ xs: '100%', lg: 'calc(66% - 1rem)' }}>
                            <HealthWidgetBase storage={storage} />
                        </Box>
                        {/* Right Column - Recent Services */}
                        <Box
                            flex="1"
                            minWidth={{ xs: '100%', md: '400px' }}
                            maxWidth={{ xs: '100%', lg: 'calc(34% - 1rem)' }}
                        >
                            {/* Recent Services Widget */}
                            <Box sx={{ minHeight: '300px' }}>
                                <RecentServicesWidget
                                    services={getRecentServices(storage.id)}
                                    loading={servicesLoading}
                                />
                            </Box>
                        </Box>
                    </Box>
                ))}
                </>
            )}



        </Container>
    );
}

export default Home;
